import { useLocation, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
	SidebarTrigger, Separator,
	Breadcrumb, BreadcrumbList, BreadcrumbItem,
	BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator,
	Avatar, AvatarFallback,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
	IconButton, NotificationButton,
	Popover, PopoverTrigger, PopoverContent,
} from '@rentflowdev/ui'
import { LogOutIcon, SettingsIcon, UserIcon } from 'lucide-react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetMeQuery, useGetPropertyQuery, useGetUnreadCountQuery } from '@/api'
import type { Property } from '@/types'
import { NotificationsDropdown } from './notifications-dropdown'
import { logout } from '@/lib/auth'
import { usePageTitleStore } from '@/lib/page-title-store'
import { getInitials } from '@/lib/text-utils'
import { NAV_ITEMS } from './nav-items'

interface RouteInfo {
	labelKey: string
	parent?: string
}

const ROUTE_MAP: Record<string, RouteInfo> = Object.fromEntries([
	...NAV_ITEMS.map((item) => [item.path, { labelKey: item.labelKey }]),
	...NAV_ITEMS.flatMap((item) =>
		item.children?.map((child) => [child.path, { labelKey: child.labelKey, parent: item.path }]) ?? []
	),
])

// Static /properties/<segment> list routes that must never be treated as a
// property id by the breadcrumb query.
const PROPERTY_LIST_SEGMENTS = new Set(['registry', 'drafts', 'archive'])


/** Build breadcrumb chain for dynamic routes like /properties/:id or /deals/:id.
 * Each crumb carries an i18n key (labelKey) except the __PAGE_TITLE__ marker. */
function buildBreadcrumbs(pathname: string, propertyStatus?: Property['status']): { labelKey: string; href?: string }[] {
	// Static route match
	const staticRoute = ROUTE_MAP[pathname]
	if (staticRoute) {
		const crumbs: { labelKey: string; href?: string }[] = []
		if (staticRoute.parent) {
			const parentRoute = ROUTE_MAP[staticRoute.parent]
			if (parentRoute) crumbs.push({ labelKey: parentRoute.labelKey, href: staticRoute.parent })
		}
		crumbs.push({ labelKey: staticRoute.labelKey })
		return crumbs
	}

	// Dynamic route: /properties/:id — parent tab depends on the property's
	// status (mirrors the sidebar branching on the detail page).
	const propertyMatch = pathname.match(/^\/properties\/([^/]+)$/)
	if (propertyMatch) {
		const parentTab =
			propertyStatus === 'DRAFT'    ? { labelKey: 'nav.propertiesDrafts', href: '/properties/drafts' } :
			propertyStatus === 'ARCHIVED' ? { labelKey: 'nav.propertiesArchive', href: '/properties/archive' } :
			                                { labelKey: 'nav.propertiesRegistry', href: '/properties/registry' }
		return [
			{ labelKey: 'nav.properties', href: '/properties' },
			parentTab,
			{ labelKey: '__PAGE_TITLE__' },
		]
	}

	// Dynamic route: /deals/:id
	const dealMatch = pathname.match(/^\/deals\/([^/]+)$/)
	if (dealMatch && dealMatch[1] !== 'active' && dealMatch[1] !== 'completed') {
		return [
			{ labelKey: 'nav.deals', href: '/deals' },
			{ labelKey: 'nav.dealsActive', href: '/deals/active' },
			{ labelKey: '__PAGE_TITLE__' },
		]
	}

	// Dynamic route: /builder/:profileId
	const builderMatch = pathname.match(/^\/builder\/([^/]+)$/)
	if (builderMatch) {
		return [
			{ labelKey: 'nav.apps', href: '/builder' },
			{ labelKey: '__PAGE_TITLE__' },
		]
	}

	// Dynamic route: /rent/:rentalId
	const rentalMatch = pathname.match(/^\/rent\/([^/]+)$/)
	if (rentalMatch && !['active', 'upcoming', 'closing', 'completed'].includes(rentalMatch[1])) {
		return [
			{ labelKey: 'nav.rent', href: '/rent' },
			{ labelKey: 'nav.rentActive', href: '/rent/active' },
			{ labelKey: '__PAGE_TITLE__' },
		]
	}

	return []
}

export function AppHeader() {
	const location = useLocation()
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { data: me } = useGetMeQuery()
	const { data: unread } = useGetUnreadCountQuery()
	const pageTitle = usePageTitleStore((s) => s.pageTitle)
	// On /properties/:id the parent crumb depends on the property's status;
	// the detail page fetches the same query, so this is served from cache.
	// The static list routes share the /properties/<segment> shape and must
	// not be treated as property ids (they 500 at the gateway, see #277).
	const propertyIdSegment = location.pathname.match(/^\/properties\/([^/]+)$/)?.[1]
	const breadcrumbPropertyId =
		propertyIdSegment && !PROPERTY_LIST_SEGMENTS.has(propertyIdSegment)
			? propertyIdSegment
			: undefined
	const { data: breadcrumbProperty } = useGetPropertyQuery(breadcrumbPropertyId ?? skipToken)
	const crumbs = buildBreadcrumbs(location.pathname, breadcrumbProperty?.status).map((c) => ({
		href: c.href,
		label: c.labelKey === '__PAGE_TITLE__' ? (pageTitle ?? '...') : t(c.labelKey),
	}))
	const isDashboard = location.pathname === '/'

	return (
		<header className="flex h-12 items-center gap-2 border-b border-(--color-border) px-4">
			<div className="flex flex-1 items-center gap-2">
				<SidebarTrigger />
				<Separator orientation="vertical" className="h-4" />
				<Breadcrumb>
					<BreadcrumbList>
						{crumbs.map((crumb, i) => {
							const isLast = i === crumbs.length - 1
							return (
								<span key={crumb.label} className="contents">
									{i > 0 && <BreadcrumbSeparator />}
									<BreadcrumbItem>
										{isLast ? (
											<BreadcrumbPage>{crumb.label}</BreadcrumbPage>
										) : (
											<BreadcrumbLink href={crumb.href!}>{crumb.label}</BreadcrumbLink>
										)}
									</BreadcrumbItem>
								</span>
							)
						})}
					</BreadcrumbList>
				</Breadcrumb>
				{isDashboard && (
					<IconButton variant="ghost" size="sm" aria-label="Настроить дашборд">
						<SettingsIcon className="size-4 text-(--color-muted-foreground)" />
					</IconButton>
				)}
			</div>

			<div className="flex items-center gap-2">
				<DropdownMenu>
					<DropdownMenuTrigger
						render={
							<button type="button" className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring)" />
						}
					>
						<Avatar className="rounded-lg cursor-pointer">
							<AvatarFallback className="rounded-lg">{getInitials(me?.name)}</AvatarFallback>
						</Avatar>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="min-w-44">
						<DropdownMenuItem onClick={() => void navigate({ to: '/settings/profile' })}>
							<UserIcon className="mr-2 size-4" />
							{t('common.profile')}
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => void navigate({ to: '/settings/profile' })}>
							<SettingsIcon className="mr-2 size-4" />
							{t('common.settings')}
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							className="text-(--color-destructive) focus:text-(--color-destructive)"
							onClick={() => { void logout() }}
						>
							<LogOutIcon className="mr-2 size-4" />
							{t('common.logout')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<Popover>
					<PopoverTrigger render={<NotificationButton aria-label={t('common.notifications')} hasNotification={(unread?.count ?? 0) > 0} />} />
					<PopoverContent>
						<NotificationsDropdown />
					</PopoverContent>
				</Popover>
			</div>
		</header>
	)
}
